import React from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import NumericInputWithValidation from './NumericInputWithValidation';
import { convertMVToAV, convertAVToMV } from '../../../../utils/conversionUtils';
import { validateAndAdjustOfferValue } from '../../../../utils/offerThresholdUtils';


// this is the cell rendered in teh datagrid for the offer and town offer input.
const NumericEditCell = (props) => {
  const { id, field, value, avToggleActive = false, row, onCommit } = props;
  const apiRef = useGridApiContext();
  const inputRef = React.useRef(null);
  const lastFloatRef = React.useRef(null); // latest numeric value from onValueChange
  // const [settlementCancellationDialogOpen, setSettlementCancellationDialogOpen] = useState(false);
  if (!row) throw new Error('[NumericEditCell] Missing row.');
  const { EQ: eq, RAR: rar } = row;
  
  // Check if case is settled
  const isSettled = row?._isSettled || false;

  // Display value (no $ added here—formatting only)
  // Now that we store AV values in the frontend, we need to convert AV to MV for display when not in AV mode
  const getDisplayValue = (val) => {
    if (val == null) {
      return '';
    }
    
    if (!avToggleActive && row) {
      // If not in AV mode, convert stored AV value to MV for display
      // Use persisted state values for EQ and RAR instead of row values
      const mvValue = convertAVToMV(val, eq, rar);
      return String(mvValue);
    }
    // If in AV mode, display the stored AV value directly
    return String(val);
  };

  // Convert displayed value back to stored AV value; return { value, wasAdjusted, adjustmentType }
  // so parent can show toast outside the grid edit overlay (avoids toast being hidden by DataGrid)
  const getStorageValue = (val) => {
    if (val == null || val === '') {
      return { value: null, wasAdjusted: false, adjustmentType: null };
    }

    const validationResult = validateAndAdjustOfferValue(
      val,
      row,
      avToggleActive,
      true
    );

    const validatedInput = validationResult.adjustedValue;
    let avValue;
    if (!avToggleActive && row) {
      avValue = convertMVToAV(validatedInput, eq, rar);
    } else {
      avValue = validatedInput;
    }

    return {
      value: avValue,
      wasAdjusted: validationResult.wasAdjusted,
      adjustmentType: validationResult.adjustmentType,
    };
  };

  const [text, setText] = React.useState(() => getDisplayValue(value));

  const commitAndStop = async (event) => {
    let final = lastFloatRef.current;

    if (final == null) {
      // Fallback: parse formatted text (commas only; $ never present)
      const cleaned = (text ?? '').replace(/[^\d.-]/g, '');
      const parsed = parseFloat(cleaned);
      final = Number.isFinite(parsed) ? parsed : null;
    }

    if (final != null) {
      const result = getStorageValue(final);
      const storageValue = result.value;

      if (storageValue !== value) {
        apiRef.current.setEditCellValue({
          id,
          field,
          value: storageValue,
        });
        if (onCommit) {
          onCommit(storageValue, value, { wasAdjusted: result.wasAdjusted, adjustmentType: result.adjustmentType });
          return;
        }
      } else if (onCommit) {
        onCommit(storageValue, value, { wasAdjusted: result.wasAdjusted, adjustmentType: result.adjustmentType });
        return;
      }
    } else if (onCommit) {
      const result = getStorageValue(null);
      apiRef.current.setEditCellValue({
        id,
        field,
        value: result.value,
      });
      onCommit(result.value, value, { wasAdjusted: false, adjustmentType: null });
      return;
    }

    // Stop cell edit mode - this will trigger processRowUpdate if no onCommit callback was provided
    apiRef.current.stopCellEditMode({ id, field });
  };

  return (
    <>
      <NumericInputWithValidation
        value={text}
        onValueChange={({ floatValue, formattedValue }) => {
          // Check if case is settled and show dialog
          // if (isSettled) {
          //   setSettlementCancellationDialogOpen(true);
          //   return;
          // }
          
          lastFloatRef.current = floatValue ?? null; // numeric only
          setText(formattedValue);                   // commas while typing
        }}
        row={row}
        isAVMode={avToggleActive}
        debounceDelay={2000}
        enableValidation={false}
        customInput={TextField}
        variant="standard"
        thousandSeparator
        decimalScale={0}
        allowNegative={false}
        isNumericString
        getInputRef={(el) => (inputRef.current = el)}
        onBlur={commitAndStop}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commitAndStop(e);
          if (e.key === 'Tab') {
            // DataGrid handles Tab before blur, stopping edit with stale editRows state.
            e.stopPropagation();
            commitAndStop(e);
          }
          if (e.key === 'Escape') {
            apiRef.current.stopCellEditMode({ id, field, ignoreModifications: true });
          }
        }}
        inputProps={{ inputMode: 'decimal' }}
        autoComplete="off"
        autoFocus
        fullWidth
        InputProps={{
          startAdornment: !avToggleActive ? (
            <InputAdornment position="start" sx={{ mr: 0, maxHeight: 'none' }}>
              <span style={{ fontWeight: 'bold' }}>$</span>
            </InputAdornment>
          ) : null,
          endAdornment: avToggleActive ? (
            <InputAdornment position="end" sx={{ ml: 0, mr: 0, flexShrink: 0 }}>
              <span style={{
                color: '#999',
                fontSize: '0.75rem',
                fontWeight: 'normal',
                opacity: 0.6,
                display: 'inline-block',
                lineHeight: 1,
              }}>
                AV
              </span>
            </InputAdornment>
          ) : null
        }}
        sx={{
          height: '100%',
          '& .MuiInput-root': {
            marginTop: 0,
            fontSize: '0.875rem',
            height: '100%',
            fontWeight: 'bold',
            opacity: isSettled ? 0.6 : 1,
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiInput-input': {
            padding: '8px 4px',
            height: '100%',
            flex: 1,
            minWidth: 0,
            fontWeight: 'bold',
          },
          '& .MuiInputAdornedStart .MuiInput-input': {
            paddingLeft: 4,
          },
          '& .MuiInputAdornedEnd .MuiInput-input': {
            paddingRight: 2,
          },
          '& .MuiInputAdornment-positionEnd': {
            marginLeft: 0,
          },
          '& .MuiInput-underline:before, & .MuiInput-underline:after, & .MuiInput-root:hover:not(.Mui-disabled):before': {
            borderBottom: 'none',
          },
          '& .MuiInput-root.Mui-focused:after': { borderBottom: '2px solid #1976d2' },
        }}
      />
    
    {/* Settlement Cancellation Dialog */}
    {/* <SettlementCancellationDialog
      open={settlementCancellationDialogOpen}
      onClose={() => setSettlementCancellationDialogOpen(false)}
      parcelId={row?.parcel_id}
      negotiations={row?.Negotiations}
      onSettlementCancelled={(updatedNegotiations) => {
        // Handle settlement cancellation - refresh the case data
        // The parent component should handle refreshing the data
      }}
    /> */}
    </>
  );
};

export default NumericEditCell;